import type { MetaDescriptor } from 'react-router';
import type { IRoute } from '../src/routes';

// Centralized SEO config, ported from the former HelmetWrapper. Each public
// route module calls `seo('<routeKey>')` from its `meta` export so the data is
// rendered into static HTML at prerender time (crawlable without JS).

const DEFAULTS = {
  siteUrl: 'https://ywt.ch',
  siteName: 'YourWebTech (ywt.ch)',
  defaultImage: 'https://ywt.ch/og-image.png',
  defaultLocale: 'en_CH',
  defaultDescription: 'YourWebTech (ywt.ch) is a swiss registrar and cloud service provider. We offer .ch and .li domains registration, whois lookup, hosting services and more.',
} as const;

type SeoEntry = {
  title: string;
  description?: string;
  canonical?: string;
  robots?: string;
  og?: { type?: 'website' | 'article'; image?: string; locale?: string };
  twitter?: { card?: 'summary' | 'summary_large_image'; image?: string };
  ldJson?: Record<string, unknown> | Record<string, unknown>[];
};

const SEO_BY_ROUTE = {
  home: {
    title: 'YourWebTech (ywt.ch) - Swiss Registrar & Cloud Solutions',
    description: DEFAULTS.defaultDescription,
    canonical: `${DEFAULTS.siteUrl}/`,
  },
  whois: {
    title: 'Whois Lookup - YourWebTech (ywt.ch)',
    description: "Look up any .ch, .li or other domain with YourWebTech's fast, minimal WHOIS tool. See the registrar, status and expiry instantly — no clutter, no sign-up.",
    canonical: `${DEFAULTS.siteUrl}/whois`,
  },
  'domains.search': {
    title: 'Search .ch/.li Domains - YourWebTech (ywt.ch)',
    description: 'Register .ch and .li domains with YourWebTech, a Swiss SWITCH-accredited registrar. Free DNSSEC, full DNS control and WHOIS privacy at honest, fair prices.',
    canonical: `${DEFAULTS.siteUrl}/domains`,
  },
  'domains.transfer': {
    title: 'Transfer a .ch/.li Domain - YourWebTech (ywt.ch)',
    description: 'Transfer your .ch or .li domain to YourWebTech in minutes, not days. Free transfers, Swiss support, free DNSSEC and full DNS control all included.',
    canonical: `${DEFAULTS.siteUrl}/domains/transfer`,
  },
  'domains.prices': {
    title: 'Domain Pricing - YourWebTech (ywt.ch)',
    description: 'Transparent .ch and .li domain pricing from YourWebTech — no hidden fees and no surprise renewals. A Swiss-accredited registrar with fair, honest prices.',
    canonical: `${DEFAULTS.siteUrl}/domains/prices`,
  },
  'hosting.static': {
    title: 'Static Website Hosting - YourWebTech (ywt.ch)',
    description: 'Host static websites in Switzerland with YourWebTech. Free HTTPS, a global CDN, custom domains and a generous free tier — deploy by ZIP upload or editor.',
    canonical: `${DEFAULTS.siteUrl}/hosting/static`,
  },
  ai: {
    title: 'Build your website with your AI assistant - YourWebTech (ywt.ch)',
    description: 'Use the ChatGPT or Claude you already have to build your website. Paste one line, answer its questions, upload one file to YWT. Free to try; pay only to publish.',
    canonical: `${DEFAULTS.siteUrl}/ai`,
  },
  'legal.terms': {
    title: 'Terms of Service - YourWebTech (ywt.ch)',
    description: 'Terms of Service for YourWebTech (ywt.ch)',
    canonical: `${DEFAULTS.siteUrl}/legal/terms-of-service`,
    og: { type: 'article' },
  },
  'legal.privacyPolicy': {
    title: 'Privacy Policy - YourWebTech (ywt.ch)',
    description: 'Privacy Policy for YourWebTech (ywt.ch)',
    canonical: `${DEFAULTS.siteUrl}/legal/privacy-policy`,
    og: { type: 'article' },
  },
  'legal.dra': {
    title: 'Domain Registration Agreement - YourWebTech (ywt.ch)',
    description: 'Domain Registration Agreement for YourWebTech (ywt.ch)',
    canonical: `${DEFAULTS.siteUrl}/legal/domain-registration-agreement`,
    og: { type: 'article' },
  },
  'legal.dpa': {
    title: 'Data Processing Agreement - YourWebTech (ywt.ch)',
    description: 'Data Processing Agreement for YourWebTech (ywt.ch)',
    canonical: `${DEFAULTS.siteUrl}/legal/data-processing-agreement`,
    og: { type: 'article' },
  },
  'legal.websiteHostingAgreement': {
    title: 'Website Hosting Agreement - YourWebTech (ywt.ch)',
    description: 'Website Hosting Agreement for YourWebTech (ywt.ch)',
    canonical: `${DEFAULTS.siteUrl}/legal/website-hosting-agreement`,
    og: { type: 'article' },
  },
} satisfies Partial<Record<IRoute, SeoEntry>>;

export type SeoRouteKey = keyof typeof SEO_BY_ROUTE;

const ORG_ID = `${DEFAULTS.siteUrl}/#organization`;
const WEBSITE_ID = `${DEFAULTS.siteUrl}/#website`;
const SCHEMA_CONTEXT = 'https://schema.org';

function resolveSeoEntry(entry: SeoEntry): SeoEntry & { description: string; canonical: string } {
  return {
    ...entry,
    description: entry.description ?? DEFAULTS.defaultDescription,
    canonical: entry.canonical ?? `${DEFAULTS.siteUrl}/`,
  };
}

/**
 * One top-level object with `@context` and an `@graph` of nodes, never a bare array: readers
 * (browser extensions among them) take `@context` from the top-level value and crash on an array.
 */
function buildLdJson(entry: SeoEntry): Record<string, unknown> | Record<string, unknown>[] {
  if (entry.ldJson) return entry.ldJson;
  const organization = {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: DEFAULTS.siteName,
    legalName: 'Cuspar GmbH',
    alternateName: ['YWT', 'YourWebTech', 'Your Web Tech', 'ywt.ch'],
    url: `${DEFAULTS.siteUrl}/`,
    logo: `${DEFAULTS.siteUrl}/logo.png`,
    image: `${DEFAULTS.siteUrl}/og-image.png`,
    email: 'support@ywt.ch',
    telephone: '+41792376067',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Bachtobelstrasse 38',
      postalCode: '8123',
      addressLocality: 'Ebmatingen',
      addressRegion: 'Zürich',
      addressCountry: 'CH',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@ywt.ch',
      telephone: '+41792376067',
      availableLanguage: ['en', 'de', 'fr', 'it'],
    },
    sameAs: ['https://www.reddit.com/r/ywt/', 'https://discord.gg/a9mAfJGSwx'],
  };
  const website = {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${DEFAULTS.siteUrl}/`,
    name: DEFAULTS.siteName,
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${DEFAULTS.siteUrl}/domains?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
  const webPage = {
    '@type': 'WebPage',
    name: entry.title,
    url: entry.canonical ?? DEFAULTS.siteUrl,
    description: entry.description ?? DEFAULTS.defaultDescription,
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORG_ID },
  };
  return { '@context': SCHEMA_CONTEXT, '@graph': [organization, website, webPage] };
}

function toDescriptors(entry: SeoEntry): MetaDescriptor[] {
  const resolved = resolveSeoEntry(entry);
  const title = resolved.title;
  const description = resolved.description;
  const canonical = resolved.canonical;
  const ogType = entry.og?.type ?? 'website';
  const ogImage = entry.og?.image ?? DEFAULTS.defaultImage;
  const ogLocale = entry.og?.locale ?? DEFAULTS.defaultLocale;
  const twitterCard = entry.twitter?.card ?? 'summary_large_image';
  const twitterImage = entry.twitter?.image ?? ogImage;

  const descriptors: MetaDescriptor[] = [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonical },
    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: DEFAULTS.siteName },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:locale', content: ogLocale },
    { name: 'twitter:card', content: twitterCard },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: twitterImage },
    { 'script:ld+json': buildLdJson({ ...entry, title, description, canonical }) },
  ];
  if (entry.robots) descriptors.push({ name: 'robots', content: entry.robots });
  return descriptors;
}

/** Meta descriptors for a public, indexable route. */
export function seo(key: SeoRouteKey): MetaDescriptor[] {
  const entry = SEO_BY_ROUTE[key];
  if (!entry) return noindex('YourWebTech (ywt.ch)');
  return toDescriptors(entry);
}

/** Meta descriptors for private/app routes that must not be indexed. */
export function noindex(title: string): MetaDescriptor[] {
  return [{ title }, { name: 'description', content: DEFAULTS.defaultDescription }, { name: 'robots', content: 'noindex, nofollow' }];
}

/**
 * Root-level default/fallback meta. Every real route overrides this with its
 * own `meta` export, so this only applies to the generated SPA fallback shell
 * (served for unknown/private client-rendered routes) — which must NOT be
 * indexable with the home page's canonical.
 */
export const DEFAULT_META: MetaDescriptor[] = noindex('YourWebTech (ywt.ch)');
